import { Controller } from "@hotwired/stimulus"

// Admin search controller - Command Palette style
export default class extends Controller {
  static targets = ["input", "overlay", "results"]

  connect() {
    // Add global keyboard shortcut for Cmd+K / Ctrl+K
    this.handleGlobalKeydown = this.globalKeydown.bind(this)
    document.addEventListener("keydown", this.handleGlobalKeydown)

    // Initialize search timeout
    this.searchTimeout = null

    // Track selected result index
    this.selectedIndex = -1
    this.resultElements = []

    // Cookie name for storing recent searches
    this.cookieName = 'admin_recent_searches'
    this.maxRecentSearches = 5

    // Track current tab and all results
    this.currentTab = 'Account'
    this.allResults = []
    this.groupedResults = {}

    // Prevent scroll chaining on results container
    if (this.hasResultsTarget) {
      this.resultsTarget.addEventListener('wheel', this.handleResultsScroll.bind(this), { passive: false })
    }
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener("keydown", this.handleGlobalKeydown)
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (this.hasResultsTarget) {
      this.resultsTarget.removeEventListener('wheel', this.handleResultsScroll)
    }
  }

  handleResultsScroll(event) {
    const target = event.currentTarget
    const atTop = target.scrollTop === 0
    const atBottom = target.scrollHeight - target.scrollTop === target.clientHeight

    // Prevent scroll chaining when at boundaries
    if ((atTop && event.deltaY < 0) || (atBottom && event.deltaY > 0)) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  globalKeydown(event) {
    // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.openCommandPalette()
      return
    }

    // Only handle these keys if overlay is open
    if (this.hasOverlayTarget && !this.overlayTarget.classList.contains('hidden')) {
      // Handle Escape key to close overlay
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeCommandPalette()
        return
      }

      // Handle arrow key navigation (only if we have results)
      if (this.resultElements.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          this.selectNext()
          return
        }

        if (event.key === 'ArrowUp') {
          event.preventDefault()
          this.selectPrevious()
          return
        }

        // Handle Enter key to navigate to selected result
        if (event.key === 'Enter' && this.selectedIndex >= 0) {
          event.preventDefault()
          this.navigateToSelected()
          return
        }
      }
    }
  }

  openCommandPalette() {
    if (!this.hasOverlayTarget || !this.hasInputTarget) return

    // Prevent body scroll when overlay is open
    document.body.style.overflow = 'hidden'

    // Show overlay
    this.overlayTarget.classList.remove('hidden')

    // Focus input after a small delay to ensure it's visible
    setTimeout(() => {
      this.inputTarget.focus()
    }, 50)

    // Show empty state
    this.showEmptyState()
  }

  closeCommandPalette() {
    if (!this.hasOverlayTarget) return

    // Re-enable body scroll
    document.body.style.overflow = ''

    // Hide overlay
    this.overlayTarget.classList.add('hidden')

    // Clear input
    if (this.hasInputTarget) {
      this.inputTarget.value = ''
    }

    // Reset tab to Account
    this.currentTab = 'Account'
    this.allResults = []
    this.groupedResults = {}

    // Reset selection
    this.selectedIndex = -1
    this.resultElements = []
  }

  handleInput(event) {
    const query = event.target.value.trim()

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // If query is less than 3 characters, show empty state
    if (query.length < 3) {
      this.showEmptyState()
      return
    }

    // Show loading state
    this.showLoadingState(query)

    // Debounce search by 300ms
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  handleKeydown(event) {
    // This method is for input-specific key handling
    // Arrow keys and Enter are now handled in globalKeydown
    // We keep this for any future input-specific needs
  }

  selectNext() {
    if (this.resultElements.length === 0) return

    // If nothing is selected yet (-1), select the first item (0)
    // Otherwise move to next item (don't wrap around at the end)
    if (this.selectedIndex < this.resultElements.length - 1) {
      this.selectedIndex = this.selectedIndex + 1
      this.updateSelection()
    }
  }

  selectPrevious() {
    if (this.resultElements.length === 0) return

    // Move to previous item (don't wrap around at the start)
    if (this.selectedIndex > 0) {
      this.selectedIndex = this.selectedIndex - 1
      this.updateSelection()
    }
  }

  updateSelection() {
    // Remove previous selection
    this.resultElements.forEach((el, index) => {
      if (index === this.selectedIndex) {
        el.classList.add('bg-blue-50', 'ring-2', 'ring-blue-500', 'ring-inset')
        el.classList.remove('hover:bg-gray-50')

        // Scroll into view if needed
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } else {
        el.classList.remove('bg-blue-50', 'ring-2', 'ring-blue-500', 'ring-inset')
        el.classList.add('hover:bg-gray-50')
      }
    })
  }

  navigateToSelected() {
    if (this.selectedIndex >= 0 && this.resultElements[this.selectedIndex]) {
      const selectedElement = this.resultElements[this.selectedIndex]
      const url = selectedElement.getAttribute('href')
      if (url) {
        window.location.href = url
      }
    }
  }

  showEmptyState() {
    if (!this.hasResultsTarget) return

    const recentSearches = this.getRecentSearches()

    if (recentSearches.length > 0) {
      // Show recent searches
      const recentHtml = recentSearches.map(query => `
        <button type="button"
                class="block w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                data-query="${this.escapeHtml(query)}"
                data-action="click->admin-search#handleRecentSearchClick">
          <div class="flex items-center">
            <svg class="h-4 w-4 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span class="text-sm text-gray-700">${this.escapeHtml(query)}</span>
          </div>
        </button>
      `).join('')

      this.resultsTarget.innerHTML = `
        <div class="h-full flex flex-col bg-gray-50">
          <div class="p-4 border-b border-gray-200">
            <p class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Recent Searches</p>
          </div>
          <div class="bg-white">
            ${recentHtml}
          </div>
          <div class="flex-1 p-6 text-center border-t border-gray-200">
            <p class="text-sm text-gray-500">Start typing to search across accounts, users, pipelines, data sources, and destinations</p>
          </div>
        </div>
      `
    } else {
      // Show default empty state
      this.resultsTarget.innerHTML = `
        <div class="p-8 text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p class="text-gray-600 font-medium">Start typing to search</p>
          <p class="text-sm text-gray-500 mt-2">Search across accounts, users, pipelines, data sources, and destinations</p>
        </div>
      `
    }
  }

  showLoadingState(query) {
    if (!this.hasResultsTarget) return

    this.resultsTarget.innerHTML = `
      <div class="p-8 text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
        <p class="text-gray-600">Searching for "${this.escapeHtml(query)}"...</p>
      </div>
    `
  }

  async performSearch(query) {
    try {
      const url = `/admin/search?q=${encodeURIComponent(query)}&filter=all`

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) throw new Error('Search failed')

      const data = await response.json()

      // Save to recent searches
      this.saveRecentSearch(query)

      // Store all results
      this.allResults = data.results

      // Group results by model type
      this.groupedResults = this.groupResultsByType(data.results)

      // Display results
      this.displayResults(query)

    } catch (error) {
      console.error('Search error:', error)
      this.displayError()
    }
  }

  groupResultsByType(results) {
    const grouped = {}
    results.forEach(result => {
      const type = result.model_type
      if (!grouped[type]) {
        grouped[type] = []
      }
      grouped[type].push(result)
    })
    return grouped
  }

  displayResults(query) {
    if (!this.hasResultsTarget) return

    if (this.allResults.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="p-8 text-center">
          <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p class="text-gray-600 font-medium">No results found for "${this.escapeHtml(query)}"</p>
          <p class="text-sm text-gray-500 mt-2">Try a different search term</p>
        </div>
      `
      return
    }

    // Reset selection to -1 (nothing selected initially)
    this.selectedIndex = -1
    this.resultElements = []

    // Ensure currentTab exists in results, otherwise default to first available type
    const availableTypes = Object.keys(this.groupedResults)
    if (!availableTypes.includes(this.currentTab) && availableTypes.length > 0) {
      // Try to default to Account, otherwise use first available
      this.currentTab = availableTypes.includes('Account') ? 'Account' : availableTypes[0]
    }

    // Build tabs HTML
    const tabsHtml = this.buildTabsHtml()

    // Get results for current tab
    const currentResults = this.groupedResults[this.currentTab] || []
    const resultsHtml = currentResults.map(result => this.buildResultHtml(result)).join('')

    this.resultsTarget.innerHTML = `
      <div class="bg-white">
        <div class="sticky top-0 z-10 flex items-center gap-1 p-4 text-xs bg-gray-50 border-b border-gray-200">
          <div class="whitespace-nowrap text-gray-500">
            Found <strong class="text-gray-700">${this.allResults.length}</strong> result${this.allResults.length !== 1 ? 's' : ''}
          </div>
          <div class="flex items-center border-l border-gray-300 pl-4 ml-2 min-w-0 flex-1">
            <div class="overflow-x-auto flex-1 -mb-px" style="scrollbar-width: thin;">
              <nav class="flex gap-2" aria-label="Tabs">
                ${tabsHtml}
              </nav>
            </div>
          </div>
        </div>
        <div class="divide-y divide-gray-200">
          ${resultsHtml}
        </div>
      </div>
    `

    // Cache result elements for keyboard navigation
    setTimeout(() => {
      this.resultElements = Array.from(this.resultsTarget.querySelectorAll('a[data-result-item]'))
    }, 0)
  }

  buildTabsHtml() {
    const types = Object.keys(this.groupedResults).sort((a, b) => {
      // Prioritize Account first
      if (a === 'Account') return -1
      if (b === 'Account') return 1
      return a.localeCompare(b)
    })

    return types.map(type => {
      const count = this.groupedResults[type].length
      const isActive = type === this.currentTab
      const activeClasses = isActive
        ? 'border-blue-500 text-blue-600 font-semibold'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'

      return `
        <button type="button"
                class="whitespace-nowrap py-2 px-3 border-b-2 text-sm ${activeClasses} transition-colors flex-shrink-0"
                data-action="click->admin-search#selectTab"
                data-type="${this.escapeHtml(type)}">
          ${this.escapeHtml(type)}s (${count})
        </button>
      `
    }).join('')
  }

  selectTab(event) {
    const type = event.currentTarget.dataset.type
    if (type && this.groupedResults[type]) {
      this.currentTab = type

      // Reset selection before switching tabs
      this.selectedIndex = -1

      // Re-render with new tab
      this.displayResults('')

      // Refresh result elements after DOM updates (longer timeout to ensure DOM is ready)
      setTimeout(() => {
        this.resultElements = Array.from(this.resultsTarget.querySelectorAll('a[data-result-item]'))
      }, 100)
    }
  }

  buildResultHtml(result) {
    const icon = this.getIconSvg(result.icon_type)

    // Build Line 1: Icon + Name + Type ID + Account
    const accountInfo = (result.account_name && result.model_type !== 'Account')
      ? `<span class="mx-2 text-gray-400">•</span><span class="text-gray-500">Account: ${this.escapeHtml(result.account_name)}</span>`
      : ''

    // Build Line 3: Metadata (if present)
    const metadataLine = result.metadata_display
      ? `<div class="text-xs text-gray-500 ml-8 mt-1">${this.escapeHtml(result.metadata_display)}</div>`
      : ''

    return `
      <a href="${result.url}"
         class="block px-4 py-3 hover:bg-gray-50 transition-colors focus:outline-none"
         data-result-item
         data-turbo="false">
        <!-- Line 1: Icon + Display Name + Type ID + Account -->
        <div class="flex items-start mb-1 text-sm">
          ${icon}
          <div class="ml-2 flex-1">
            <span class="font-semibold text-gray-900">${this.escapeHtml(result.display_name)}</span>
            <span class="mx-2 text-gray-400">•</span>
            <span class="text-gray-600">${this.escapeHtml(result.model_type)} ${result.searchable_id}</span>
            ${accountInfo}
          </div>
        </div>

        <!-- Line 2: Matched content -->
        <div class="text-xs text-gray-600 ml-8">
          Matched: ${result.highlighted_match}
        </div>

        <!-- Line 3: Metadata (conditional) -->
        ${metadataLine}
      </a>
    `
  }

  getIconSvg(iconType) {
    const icons = {
      // Account icon - Building
      'account': `
                <svg class="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 21V4H16V21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M20 9H23V21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M10 17V21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M8 12L8 13" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M8 8L8 9" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M11.9998 12L11.9998 13" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M11.9998 8L11.9998 9" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M1 21H19" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                </svg>
            `,
      // User icon - Person
      'user': `
                <svg class="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M23 20.9999V18.9999C22.9993 18.1136 22.7044 17.2527 22.1614 16.5522C21.6184 15.8517 20.8581 15.3515 20 15.1299" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M16 3.12988C16.8604 3.35018 17.623 3.85058 18.1676 4.55219C18.7122 5.2538 19.0078 6.11671 19.0078 7.00488C19.0078 7.89305 18.7122 8.75596 18.1676 9.45757C17.623 10.1592 16.8604 10.6596 16 10.8799" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                </svg>
            `,
      // Pipeline icon - Flow with darker paths
      'pipeline': `
                <svg class="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12H21" stroke="currentColor" stroke-linejoin="round" stroke-dasharray="4 4"/>
                  <path d="M21 9H15.5C14.8705 9 14.2777 8.70361 13.9 8.19999L10.6 3.80001C10.2223 3.29639 9.62954 3 9.00002 3H3" stroke="currentColor" stroke-linejoin="round"/>
                  <path d="M3 15H6.5C7.12951 15 7.72229 15.2964 8.1 15.8L11.4 20.2C11.7777 20.7036 12.3705 21 13 21H21" stroke="currentColor" stroke-linejoin="round"/>
                </svg>
            `,
      // Data Source icon - Flow (lighter)
      'data_source': `
                <svg class="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12H21" stroke="currentColor" stroke-linejoin="round" stroke-dasharray="4 4"/>
                  <path d="M21 9H15.5C14.8705 9 14.2777 8.70361 13.9 8.19999L10.6 3.80001C10.2223 3.29639 9.62954 3 9.00002 3H3" stroke="currentColor" stroke-linejoin="round"/>
                  <path d="M3 15H6.5C7.12951 15 7.72229 15.2964 8.1 15.8L11.4 20.2C11.7777 20.7036 12.3705 21 13 21H21" stroke="currentColor" stroke-linejoin="round"/>
                </svg>
            `,
      // Data Destination icon - Flow
      'data_destination': `
                <svg class="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12H21" stroke="currentColor" stroke-linejoin="round" stroke-dasharray="4 4"/>
                  <path d="M21 9H15.5C14.8705 9 14.2777 8.70361 13.9 8.19999L10.6 3.80001C10.2223 3.29639 9.62954 3 9.00002 3H3" stroke="currentColor" stroke-linejoin="round"/>
                  <path d="M3 15H6.5C7.12951 15 7.72229 15.2964 8.1 15.8L11.4 20.2C11.7777 20.7036 12.3705 21 13 21H21" stroke="currentColor" stroke-linejoin="round"/>
                </svg>
            `,
      // Predict model icon - Trend line
      'predict/model_pipeline': `
                <svg class="h-5 w-5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 3V21H21" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                  <path d="M7 15L11 10L14 13L20 6" stroke="currentColor" stroke-linecap="square" stroke-linejoin="round"/>
                </svg>
            `
    }

    return icons[iconType] || icons['account']
  }

  displayError() {
    if (!this.hasResultsTarget) return

    this.resultsTarget.innerHTML = `
      <div class="p-8 text-center">
        <svg class="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-gray-600 font-medium">An error occurred while searching</p>
        <p class="text-sm text-gray-500 mt-2">Please try again</p>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Recent searches cookie management
  getRecentSearches() {
    const cookie = this.getCookie(this.cookieName)
    if (!cookie) return []

    try {
      return JSON.parse(decodeURIComponent(cookie))
    } catch (e) {
      return []
    }
  }

  saveRecentSearch(query) {
    let recent = this.getRecentSearches()

    // Remove if already exists (we'll add it to the front)
    recent = recent.filter(q => q !== query)

    // Add to front
    recent.unshift(query)

    // Keep only the last N searches
    recent = recent.slice(0, this.maxRecentSearches)

    // Save to cookie (expires in 30 days)
    this.setCookie(this.cookieName, encodeURIComponent(JSON.stringify(recent)), 30)
  }

  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
  }

  setCookie(name, value, days) {
    const expires = new Date()
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000))
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
  }

  handleRecentSearchClick(event) {
    event.preventDefault()
    const query = event.currentTarget.dataset.query
    if (query && this.hasInputTarget) {
      this.inputTarget.value = query
      this.handleInput({ target: this.inputTarget })
    }
  }
}
